/**
 * OfflineBanner.jsx
 * ===================
 * Bandeau persistant en haut de l'écran indiquant :
 *  - "📴 Hors-ligne — Vos modifications sont sauvegardées localement" si pas réseau
 *  - "🔄 X découverte(s) en attente de sync · Synchroniser" si pending > 0 ET online
 *
 * Masqué quand online ET pending=0.
 */
import React from 'react';
import { CloudOff, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { useOffline } from '../../contexts/OfflineContext';

/* eslint-disable react/no-unescaped-entities */

export default function OfflineBanner() {
    const { isOnline, pendingCount, syncing, syncNow } = useOffline();

    if (isOnline && pendingCount === 0 && !syncing) return null;

    // OFFLINE state
    if (!isOnline) {
        return (
            <div
                className="sticky top-0 z-[85] w-full bg-gradient-to-r from-amber-600 to-amber-500 text-white px-4 py-2 flex items-center justify-between gap-3 shadow-lg"
                data-testid="offline-banner"
            >
                <div className="flex items-center gap-2 min-w-0">
                    <CloudOff className="w-4 h-4 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-bold truncate">
                        Hors-ligne — Vos modifications sont sauvegardées localement
                    </span>
                </div>
                {pendingCount > 0 && (
                    <span className="text-[10px] sm:text-xs font-bold whitespace-nowrap px-2 py-0.5 rounded-full bg-white/20">
                        {pendingCount} en attente
                    </span>
                )}
            </div>
        );
    }

    // ONLINE + pending state
    if (pendingCount > 0 || syncing) {
        return (
            <div
                className="sticky top-0 z-[85] w-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-white px-4 py-2 flex items-center justify-between gap-3 shadow-lg"
                data-testid="sync-banner"
            >
                <div className="flex items-center gap-2 min-w-0">
                    {syncing ? (
                        <RefreshCw className="w-4 h-4 flex-shrink-0 animate-spin" />
                    ) : (
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    )}
                    <span className="text-xs sm:text-sm font-bold truncate">
                        {syncing
                            ? 'Synchronisation en cours…'
                            : `${pendingCount} découverte${pendingCount > 1 ? 's' : ''} en attente de sync`}
                    </span>
                </div>
                {!syncing && (
                    <button
                        type="button"
                        onClick={() => syncNow()}
                        className="text-[10px] sm:text-xs font-bold whitespace-nowrap px-3 py-1 rounded-full bg-white text-[#681793] hover:bg-white/90 transition active:scale-95"
                        data-testid="sync-now-btn"
                    >
                        Synchroniser
                    </button>
                )}
            </div>
        );
    }

    return null;
}
